import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ToastContext from '../contexts/ToastContext';
import MapLocationPicker from './MapLocationPicker';
import { getEvent, createEvent, updateEvent } from '../utils/eventsApi';

const ICONS = {
  date: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  time: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  ),
  location: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <path d="M12 2C8 2 5 5 5 9c0 3.9 5 11 7 13 2-2 7-9.1 7-13 0-4-3-7-7-7z" />
      <circle cx="12" cy="9" r="2" />
    </svg>
  ),
  sport: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l4 2" />
    </svg>
  ),
  level: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <path d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.402 8.168L12 18.896 4.664 23.165l1.402-8.168L.132 9.21l8.2-1.192z" />
    </svg>
  ),
  participants: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <path d="M17 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  payment: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  ),
  price: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M9 12h6" />
    </svg>
  ),
  description: (
    <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="false">
      <path d="M3 21v-6a2 2 0 012-2h7" />
      <path d="M21 15v6H9" />
      <path d="M7 7h10M7 11h6" />
    </svg>
  ),
};

const getSteps = (isPaid) => {
  const baseSteps = [
    { key: 'date', label: 'Date', icon: ICONS.date, question: 'Quand voulez-vous organiser votre événement ?' },
    { key: 'startTime', label: 'Heure début', icon: ICONS.time, question: 'À quelle heure souhaitez-vous commencer ?' },
    { key: 'endTime', label: 'Heure fin', icon: ICONS.time, question: 'À quelle heure se terminera l\'événement ?' },
    { key: 'location', label: 'Lieu', icon: ICONS.location, question: 'Où se déroulera l\'événement ?' },
    { key: 'sportType', label: 'Sport', icon: ICONS.sport, question: 'Quel sport pratiquez-vous ?' },
    { key: 'level', label: 'Niveau', icon: ICONS.level, question: 'Quel niveau requis ?' },
    { key: 'maxParticipants', label: 'Participants', icon: ICONS.participants, question: 'Combien de participants maximum ?' },
    { key: 'isPaid', label: 'Paiement', icon: ICONS.payment, question: 'Cet événement est-il payant ?' },
  ];
  
  if (isPaid) {
    baseSteps.push({ key: 'price', label: 'Prix', icon: ICONS.price, question: 'Quel est le prix d\'inscription ?' });
  }
  
  baseSteps.push({ key: 'description', label: 'Description', icon: ICONS.description, question: 'Description de l\'événement' });
  return baseSteps;
};

export default function CreateEvent() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [locationState, setLocationState] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [sportType, setSportType] = useState('');
  const [description, setDescription] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('0');
  const [level, setLevel] = useState('Tous niveaux');
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const toast = useContext(ToastContext);
  const [editing, setEditing] = useState(false);
  const [eventId, setEventId] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [mapExpanded, setMapExpanded] = useState(false);
  const STEPS = useMemo(() => getSteps(isPaid), [isPaid]);

  const today = new Date().toISOString().split('T')[0];
  const oneYearLater = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const currentTime = new Date().toTimeString().slice(0,5);

  useEffect(() => {
    const eventIdFromUrl = searchParams.get('eventId');
    if (eventIdFromUrl) {
      setLoadingEvent(true);
      setEventId(eventIdFromUrl);
      getEvent(eventIdFromUrl)
        .then(event => {
          setDate(new Date(event.date).toISOString().split('T')[0]);
          setStartTime(event.startTime);
          setEndTime(event.endTime || '');
          setLocationState(event.location);
          setLatitude(event.latitude || null);
          setLongitude(event.longitude || null);
          setSportType(event.sportType);
          setDescription(event.description || '');
          setMaxParticipants(event.maxParticipants || '1');
          setLevel(event.level || 'Tous niveaux');
          setIsPaid(event.isPaid || false);
          setPrice(event.price || '');
          setEditing(true);
          setLoadingEvent(false);
        })
        .catch(err => {
          console.error('Erreur lors du chargement de l\'événement:', err);
          if (toast && toast.error) toast.error('Erreur lors du chargement de l\'événement');
          setLoadingEvent(false);
        });
    } else if (location.state && location.state.event) {
      const event = location.state.event;
      setDate(new Date(event.date).toISOString().split('T')[0]);
      setStartTime(event.startTime);
      setEndTime(event.endTime);
      setLocationState(event.location);
      setLatitude(event.latitude || null);
      setLongitude(event.longitude || null);
      setSportType(event.sportType);
      setDescription(event.description);
      setMaxParticipants(event.maxParticipants);
      setLevel(event.level);
      setIsPaid(event.isPaid);
      setPrice(event.price || '');
      setEditing(true);
      setEventId(event._id);
    }
  }, [location.state, searchParams, toast]);

  if (!currentUser) {
    return <div>Connectez-vous pour créer un événement.</div>;
  }

  if (loadingEvent) {
    return <div className="container">Chargement de l'événement...</div>;
  }

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 'date':
        if (!date) newErrors.date = 'La date est requise';
        break;
      case 'startTime':
        if (!startTime) newErrors.startTime = 'L\'heure de début est requise';
        break;
      case 'endTime':
        // endTime is optional, no validation needed
        break;
      case 'location':
        if (!locationState) newErrors.location = 'Le lieu est requis';
        break;
      case 'sportType':
        if (!sportType) newErrors.sportType = 'Le sport est requis';
        break;
      case 'price':
        if (isPaid && !price) newErrors.price = 'Le prix est requis lorsque l\'événement est payant';
        if (price && isNaN(parseFloat(price))) newErrors.price = 'Le prix doit être un nombre valide';
        break;
      default:
        break;
    }
    
    return newErrors;
  };

  const canProceedToNext = () => {
    const currentKey = STEPS[currentStep].key;
    const stepErrors = validateStep(currentKey);
    
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (canProceedToNext()) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentStep(STEPS.length); // Aller à la page récapitulatif
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleIsPaidChange = (checked) => {
    setIsPaid(checked);
    // Ensure currentStep doesn't exceed the new STEPS length
    const newSteps = getSteps(checked);
    if (currentStep >= newSteps.length) {
      setCurrentStep(newSteps.length - 1);
    }
    // Clear price if unchecking isPaid
    if (!checked) {
      setPrice('');
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    // Validation complète
    const eventDateTime = new Date(`${date}T${startTime}`);
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    if (eventDateTime <= oneHourFromNow) {
      setMessage('L\'événement doit être programmé au moins 1 heure dans le futur.');
      setLoading(false);
      return;
    }

    // ensure we have precise coordinates, otherwise the map can't display it
    if (latitude === null || longitude === null) {
      setMessage('Veuillez sélectionner un lieu précis sur la carte (ou utiliser votre position).');
      setLoading(false);
      return;
    }

    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(now.getFullYear() + 1);
    if (eventDateTime > oneYearFromNow) {
      setMessage('La date ne peut pas être plus d\'un an dans le futur.');
      setLoading(false);
      return;
    }

    // Validation de l'heure de fin
    if (endTime && startTime >= endTime) {
      setMessage('L\'heure de fin doit être après l\'heure de début.');
      setLoading(false);
      return;
    }

    try {
      const eventData = {
        date,
        startTime,
        endTime: endTime || undefined,
        location: locationState,
        latitude: latitude || undefined,
        longitude: longitude || undefined,
        sportType,
        description,
        maxParticipants: maxParticipants ? parseInt(maxParticipants) : 0,
        level,
        isPaid,
        price: isPaid && price ? parseFloat(price) : undefined
      };

      if (editing) {
        await updateEvent(eventId, eventData);
        setMessage('Événement modifié avec succès');
        if (toast && toast.success) toast.success('Événement modifié');
        setTimeout(() => {
          window.location.href = '/events';
        }, 1500);
      } else {
        await createEvent(eventData, currentUser.id || currentUser._id);
        setMessage('Événement créé avec succès');
        if (toast && toast.success) toast.success('Événement créé');
        setTimeout(() => {
          window.location.href = '/events';
        }, 1500);
      }
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erreur lors de la création/modification';
      setMessage(msg);
      if (toast && toast.error) toast.error(msg);
    }
    setLoading(false);
  }

  const renderStepContent = () => {
    const step = STEPS[currentStep];
    const today = new Date().toISOString().split('T')[0];
    const oneYearLater = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const currentTime = new Date().toTimeString().slice(0, 5);

    switch (step.key) {
      case 'date':
        return (
          <div className="field">
            <input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} min={today} max={oneYearLater} onKeyDown={(e) => e.preventDefault()} required autoFocus />
            {errors.date && <small style={{color: 'var(--error)'}}>⚠️ {errors.date}</small>}
          </div>
        );
      case 'startTime':
        return (
          <div className="field">
            <input className="input" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} min={date === today ? currentTime : undefined} onKeyDown={(e) => e.preventDefault()} required autoFocus />
            {errors.startTime && <small style={{color: 'var(--error)'}}>⚠️ {errors.startTime}</small>}
          </div>
        );
      case 'endTime':
        return (
          <div className="field">
            <input className="input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} min={startTime || undefined} onKeyDown={(e) => e.preventDefault()} placeholder="Optionnel" />
            <small className="muted" style={{marginTop: 4, display: 'block'}}>Laissez vide si non applicable</small>
          </div>
        );
      case 'location':
        return (
          <div className="field">
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'}}>
              <input 
                className="input" 
                type="text" 
                value={locationState} 
                onChange={e => setLocationState(e.target.value)} 
                required 
                placeholder="Ex: Stade Municipal, Paris" 
                style={{flex: 1}}
                onClick={() => setMapExpanded(true)}
                readOnly
              />
              <button
                type="button"
                onClick={() => setMapExpanded(!mapExpanded)}
                className="use-position-btn"
              >
                📍
              </button>
            </div>
            {mapExpanded && (
              <MapLocationPicker
                latitude={latitude}
                longitude={longitude}
                address={locationState}
                onLocationChange={(data) => {
                  if (data.latitude !== undefined) setLatitude(data.latitude);
                  if (data.longitude !== undefined) setLongitude(data.longitude);
                  if (data.address) setLocationState(data.address);
                }}
                isExpanded={mapExpanded}
              />
            )}
            {errors.location && <small style={{color: 'var(--error)'}}>⚠️ {errors.location}</small>}
          </div>
        );
      case 'sportType':
        return (
          <div className="field">
            <select className="input" value={sportType} onChange={e => setSportType(e.target.value)} required autoFocus>
              <option value="">Sélectionnez un sport</option>
              <option value="Football">Football</option>
              <option value="Basketball">Basketball</option>
              <option value="Tennis">Tennis</option>
              <option value="Natation">Natation</option>
              <option value="Course">Course</option>
              <option value="Cyclisme">Cyclisme</option>
              <option value="Autre">Autre</option>
            </select>
            {errors.sportType && <small style={{color: 'var(--error)'}}>⚠️ {errors.sportType}</small>}
          </div>
        );
      case 'level':
        return (
          <div className="field">
            <select className="input" value={level} onChange={e => setLevel(e.target.value)} autoFocus>
              <option value="Tous niveaux">Tous niveaux</option>
              <option value="Débutant">Débutant</option>
              <option value="Intermédiaire">Intermédiaire</option>
              <option value="Avancé">Avancé</option>
            </select>
          </div>
        );
      case 'maxParticipants':
        return (
          <div className="field">
            <input className="input" type="number" value={maxParticipants} onChange={e => setMaxParticipants(e.target.value)} min="0" placeholder="0 = illimité" autoFocus />
            <small className="muted" style={{marginTop: 4, display: 'block'}}>Entrez 0 pour illimité</small>
          </div>
        );
      case 'isPaid':
        return (
          <div className="field" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0'}}>
            <label style={{margin: 0, fontSize: '1.1rem'}}>
              Cet événement est payant
            </label>
            <input type="checkbox" checked={isPaid} onChange={e => handleIsPaidChange(e.target.checked)} autoFocus style={{width: '24px', height: '24px', cursor: 'pointer'}} />
          </div>
        );
      case 'price':
        return (
          <div className="field">
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span style={{fontSize: '1.1rem', fontWeight: '500'}} aria-hidden="true">{ICONS.price}</span>
              <input className="input" type="number" value={price} onChange={e => setPrice(e.target.value)} min="0" step="0.01" placeholder="0.00" autoFocus style={{flex: 1}} />
              <span style={{fontSize: '0.95rem', fontWeight: '500'}}>€</span>
            </div>
            {errors.price && <small style={{color: 'var(--error)'}}>⚠️ {errors.price}</small>}
            <small className="muted" style={{marginTop: 4, display: 'block'}}>Entrez le prix en euros</small>
          </div>
        );
      case 'description':
        return (
          <div className="field">
            <textarea className="input" value={description} onChange={e => setDescription(e.target.value)} rows="4" placeholder="Décrivez votre événement..." autoFocus />
            <small className="muted" style={{marginTop: 4, display: 'block'}}>Optionnel - décrivez votre événement, vos attentes, etc.</small>
          </div>
        );
      default:
        return null;
    }
  };

  const renderSummary = () => {
    const formatDate = (d) => new Date(`${d}T00:00`).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    
    return (
      <div style={{animation: 'fadeIn 0.3s ease-in'}}>
        <h3 style={{marginBottom: '20px', textAlign: 'center', fontSize: '1.3rem'}}>Récapitulatif de votre événement</h3>
        
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px'}}>
          <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px'}}>
            <small className="muted" style={{fontSize: '0.85rem'}} title="Date" aria-label="Date">{ICONS.date}</small>
            <div style={{fontSize: '1.1rem', fontWeight: '500', marginTop: '4px'}}>{formatDate(date)}</div>
          </div>
          
          <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px'}}>
            <small className="muted" style={{fontSize: '0.85rem'}} title="Heure" aria-label="Heure">{ICONS.time}</small>
            <div style={{fontSize: '1.1rem', fontWeight: '500', marginTop: '4px'}}>
              {startTime}{endTime ? ` - ${endTime}` : ''}
            </div>
          </div>
          
          <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px'}}>
            <small className="muted" style={{fontSize: '0.85rem'}} title="Sport" aria-label="Sport">{ICONS.sport}</small>
            <div style={{fontSize: '1.1rem', fontWeight: '500', marginTop: '4px'}}>{sportType}</div>
          </div>
          
          <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px'}}>
            <small className="muted" style={{fontSize: '0.85rem'}} title="Niveau" aria-label="Niveau">{ICONS.level}</small>
            <div style={{fontSize: '1.1rem', fontWeight: '500', marginTop: '4px'}}>{level}</div>
          </div>
          
          <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px'}}>
            <small className="muted" style={{fontSize: '0.85rem'}} title="Participants" aria-label="Participants">{ICONS.participants}</small>
            <div style={{fontSize: '1.1rem', fontWeight: '500', marginTop: '4px'}}>{maxParticipants === '0' ? 'Illimité' : maxParticipants}</div>
          </div>
          
          <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px'}}>
            <small className="muted" style={{fontSize: '0.85rem'}} title="Paiement" aria-label="Paiement">{ICONS.payment}</small>
            <div style={{fontSize: '1.1rem', fontWeight: '500', marginTop: '4px'}}>{isPaid ? `💳 ${parseFloat(price).toFixed(2)}€` : '🆓 Gratuit'}</div>
          </div>
        </div>
        
        <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px', marginBottom: '20px'}}>
          <small className="muted" style={{fontSize: '0.85rem'}}>Lieu</small>
          <div style={{fontSize: '1.1rem', fontWeight: '500', marginTop: '4px'}}>{locationState}</div>
          {latitude && longitude && (
            <div style={{fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '8px'}}>
              📍 {latitude.toFixed(4)}, {longitude.toFixed(4)}
            </div>
          )}
        </div>
        
        {description && (
          <div className="card" style={{background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '12px', marginBottom: '20px'}}>
            <small className="muted" style={{fontSize: '0.85rem'}}>Description</small>
            <div style={{fontSize: '1rem', marginTop: '8px', lineHeight: '1.5'}}>{description}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container" style={{minHeight: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 12px', marginBottom: '80px'}}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .create-event-card {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .progress-bar-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
        }
        .progress-bar-container::before,
        .progress-bar-container::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 2px;
          transform: translateY(-50%);
          pointer-events: none;
        }
        .progress-bar-container::before {
          background: var(--border);
          z-index: 1;
        }
        .progress-bar-container::after {
          background: var(--accent);
          width: var(--progress);
          transition: width 0.3s ease;
          z-index: 1;
        }
        .progress-step {
          flex: 1;
          text-align: center;
          position: relative;
          z-index: 2;
        }
        .progress-step-circle {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--bg);
          color: var(--muted);
          border: 2px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 8px;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.22s ease;
          cursor: pointer;
        }
        .progress-step-circle.active {
          background: linear-gradient(90deg, var(--accent), var(--accent-2));
          color: white;
          border: none;
          box-shadow: 0 8px 24px rgba(60,78,90,0.12);
        }
        .progress-step-circle.completed {
          background: linear-gradient(90deg, var(--accent), var(--accent-2));
          color: white;
          border: none;
          box-shadow: 0 8px 24px rgba(60,78,90,0.12);
        }
        .progress-step-label {
          font-size: 0.75rem;
          color: var(--text-secondary);
          white-space: nowrap;
          overflow: visible;
          text-overflow: ellipsis;
          transition: color 0.3s ease;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 28px;
        }
        .progress-step-label svg.icon-svg { width: 24px; height: 24px; stroke: currentColor; stroke-width: 1.5; }
        .icon-svg { width: 24px; height: 24px; vertical-align: middle; display: block; flex-shrink: 0; }
        .progress-step.active .progress-step-label {
          color: var(--accent);
          font-weight: 600;
        }
        .progress-step.completed .progress-step-label {
          color: var(--success);
        }
        .step-card {
          animation: slideIn 0.4s ease-out;
          min-height: auto;
          display: flex;
          flex-direction: column;
        }
        .button-group {
          display: flex;
          gap: 12px;
          margin-top: auto;
          padding-top: 20px;
          border-top: 1px solid var(--border);
        }
        .btn-nav {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid var(--accent);
          background: transparent;
          color: var(--accent);
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
          font-size: 1rem;
        }
        .btn-nav:hover:not(:disabled) {
          background: var(--accent);
          color: white;
          border-color: var(--accent);
        }
        .btn-nav:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-next {
          background: var(--accent);
          color: white;
          border-color: var(--accent);
        }
        .btn-next:hover:not(:disabled) {
          filter: brightness(0.92);
        }
        /* bouton pour utiliser ma position (même couleur que Explorer) */
        .use-position-btn {
          padding: 0;
          width: 38px;
          height: 38px;
          background: var(--accent);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1.2rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .use-position-btn:hover {
          filter: brightness(0.92);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .use-position-btn:active {
          transform: translateY(0);
        }
        @media (max-width: 640px) {
          .progress-bar-container {
            margin-bottom: 20px;
          }
          .progress-step-label {
            font-size: 0.65rem;
          }
          .step-card {
            min-height: auto;
          }
          .button-group {
            padding-top: 20px;
          }
        }
      `}</style>

      <div className="card create-event-card" style={{width: '100%', maxWidth: '600px', animation: 'fadeIn 0.3s ease-in'}}>
        <h2 style={{margin: 0, textAlign: 'center'}}>
          {editing ? 'Modifier l\'événement' : 'Créer un événement'}
        </h2>

        {message && (
          <div role="alert" className="card headerless" style={{
            background: message.includes('succès') ? 'rgba(16,185,129,0.04)' : 'rgba(239,68,68,0.04)',
            borderLeft: `4px solid ${message.includes('succès') ? 'var(--success)' : 'var(--error)'}`,
            marginBottom: '20px',
            animation: 'fadeIn 0.3s ease'
          }}>
            <div style={{color: message.includes('succès') ? 'var(--success)' : 'var(--error)'}}>
              {message.includes('succès') ? '✅' : '❌'} {message}
            </div>
          </div>
        )}

        {/* Barre de progression */}
        <div
          className="progress-bar-container"
          style={{
            "--progress": currentStep === STEPS.length ? '100%' : `${(currentStep / STEPS.length) * 100}%`
          }}
        >
          {STEPS.map((s, idx) => (
            <div key={idx} className={`progress-step ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}>
              <div className={`progress-step-circle ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`} aria-hidden="false">
                {idx + 1}
              </div>
              <div className="progress-step-label" title={s.label} aria-label={s.label} style={{fontSize: '1.1rem'}}>{s.icon}</div>
            </div>
          ))}
        </div>

        {/* Contenu des étapes */}
        {currentStep < STEPS.length ? (
          <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="step-card">
            <div style={{marginBottom: '24px'}}>
              <h3 style={{fontSize: '1.2rem', marginBottom: '16px', color: 'var(--text)'}}>
                {STEPS[currentStep].question}
              </h3>
              {renderStepContent()}
            </div>

            <div className="button-group">
              <button
                type="button"
                className="btn-nav"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                ← Retour
              </button>
              <button
                type="submit"
                className="btn-nav btn-next"
              >
                {currentStep === STEPS.length - 1 ? 'Récapitulatif →' : 'Continuer →'}
              </button>
            </div>
          </form>
        ) : (
          <div className="step-card">
            {renderSummary()}

            <div className="button-group">
              <button
                type="button"
                className="btn-nav"
                onClick={() => setCurrentStep(STEPS.length - 1)}
              >
                ← Retour
              </button>
              <button
                type="submit"
                className="btn-nav btn-next"
                disabled={loading}
                onClick={handleSubmit}
              >
                {loading ? (editing ? '⏳ Modification...' : '⏳ Création...') : (editing ? '✓ Modifier' : '✓ Créer')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}